import React, { useState, useEffect } from 'react';
import ChatBubble from '../Components/Chatbubble';
import MessageInput from '../Components/MessageInput';
import aiAvatar from '../Assets/kumar-avatar.png';
import userAvatar from '../Assets/User-avatar.png';
import ProgressBar from '../Components/CareerProgressBar';
import TypingDots from '../Components/TypingDots';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { padding: 30 },
  section: { marginBottom: 10 },
  heading: { fontSize: 16, marginBottom: 10 },
  text: { fontSize: 12, lineHeight: 1.5 },
});

const questions = [
  '⏳ How much time can you dedicate each day?',
  '📚 What’s your current knowledge level? (Beginner, Intermediate, or Advanced)',
  '⚡ Are you a fast learner or a slow learner?',
  '🎓 Do you prefer mentorship or can you learn from free resources like YouTube?',
];

const buildPdfBlob = async (career, split) => {
  const doc = (
    <Document>
      <Page style={styles.page}>
        <Text style={styles.heading}>Career Roadmap: {career}</Text>
        {split.map((step, idx) => (
          <View key={idx} style={styles.section}>
            <Text style={styles.text}>{step}</Text>
          </View>
        ))}
      </Page>
    </Document>
  );
  const asPdf = pdf([]);
  asPdf.updateContainer(doc);
  const blob = await asPdf.toBlob();
  return blob;
};

const CareerProgress = () => {
  const [chatLog, setChatLog] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [career, setCareer] = useState('');
  const [answers, setAnswers] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [allLevels, setAllLevels] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [waitingForReflection, setWaitingForReflection] = useState(false);

  useEffect(() => {
    setChatLog([
      {
        sender: 'ai',
        avatar: aiAvatar,
        name: 'Kumar',
        message:
          '🎯 Hey! What career are you curious about today? I’ll guide you step-by-step 🚀',
      },
    ]);
  }, []);

  const handleUserInput = async (text) => {
    setIsTyping(true);
    const userMsg = { sender: 'user', avatar: userAvatar, name: 'You', message: text };
    let updatedLog = [...chatLog, userMsg];
    setChatLog(updatedLog);

    if (!career) {
      setCareer(text);
      setChatLog((prev) => [
        ...prev,
        { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: `Awesome! You're aiming for **${text}** 🌟 Let me ask you a few quick questions.` },
        { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: questions[0] },
      ]);
      setQuestionIndex(1);
      setIsTyping(false);
      return;
    }

    if (questionIndex > 0 && questionIndex <= questions.length) {
      const keys = ['time', 'level', 'pace', 'style'];
      setAnswers((prev) => ({ ...prev, [keys[questionIndex - 1]]: text }));

      if (questionIndex < questions.length) {
        setChatLog((prev) => [
          ...prev,
          { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: questions[questionIndex] },
        ]);
        setQuestionIndex((i) => i + 1);
        setIsTyping(false);
        return;
      } else {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/api/career-roadmap-full?career=${encodeURIComponent(career)}`
          );
          
          const data = await res.json();

          if (data.roadmap) {
            const split = data.roadmap
              .split(/\*\*Level \d: /)
              .filter(Boolean)
              .map((val, idx) => `**Level ${idx + 1}:** ${val.trim()}`);

            setAllLevels(split);

            // 🛠 Create PDF + force mobile-friendly download
            const blob = await buildPdfBlob(career, split);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `career-roadmap-${career}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            setChatLog((prev) => [
              ...prev,
              {
                sender: 'ai',
                avatar: aiAvatar,
                name: 'Kumar',
                message: `Thanks for answering! Here's your **5-level career roadmap** 🗺️. Let’s start with **Level 1** now.`,
              },
              {
                sender: 'ai',
                avatar: aiAvatar,
                name: 'Kumar',
                message: split[0],
              },
              {
                sender: 'ai',
                avatar: aiAvatar,
                name: 'Kumar',
                message: `🔍 After you try these steps, let me know what you learned! Just share your reflection below.`,
              },
            ]);

            setCurrentLevel(1);
            setWaitingForReflection(true);
          } else {
            throw new Error('No roadmap returned');
          }
        } catch (err) {
          setChatLog((prev) => [
            ...prev,
            { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: `Oops! Something went wrong while fetching your roadmap.` },
          ]);
        }
        setIsTyping(false);
        return;
      }
    }

    if (waitingForReflection) {
      const nextLevel = currentLevel + 1;
      if (nextLevel <= 5) {
        setChatLog((prev) => [
          ...prev,
          { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: `👏 Great reflection! Let’s move to **Level ${nextLevel}** 🔥` },
          { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: allLevels[nextLevel - 1] },
          { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: `📝 When you’re done, tell me what you learned so we can continue.` },
        ]);
        setCurrentLevel(nextLevel);
      } else {
        setChatLog((prev) => [
          ...prev,
          { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: `🎉 You've completed **all 5 levels**! I'm so proud of you. Now go rock that career! 💼🚀` },
        ]);
        setWaitingForReflection(false);
      }
      setIsTyping(false);
      return;
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-[30px] shadow-xl p-6 flex flex-col justify-between min-h-[75vh] max-h-[90vh] w-full max-w-[950px] mx-auto overflow-hidden">
      <div className="flex flex-col gap-6 overflow-y-auto flex-grow pr-2">
        <ProgressBar currentLevel={currentLevel} />

        {chatLog.map((msg, idx) => (
          <ChatBubble key={idx} {...msg} />
        ))}

        {isTyping && (
          <ChatBubble sender="ai" avatar={aiAvatar} name="Kumar" message={<TypingDots />} />
        )}
      </div>

      <div className="mt-6">
        <MessageInput onSend={handleUserInput} />
      </div>
    </div>
  );
};

export default CareerProgress;
